/**
 * DashScope Qwen-Image-2.0-Pro（图生图：同步）
 *
 * 1) POST https://dashscope-intl.aliyuncs.com/api/v1/services/aigc/multimodal-generation/generation
 * 2) 解析 output.choices[].message.content[].image 的 URLs
 * 3) 下载每张图片为 File
 * 4) 调用业务上传接口 uploadFile(file) 获取可长期访问的 URL
 * 5) 返回 { urls, provider:'official', sync:true }
 */
import axios from 'axios'
import { extractApiErrorMessage } from '../../extract-api-error-message'

var GENERATION_PATH = '/api/v1/services/aigc/multimodal-generation/generation'
var DEFAULT_MODEL = 'qwen-image-2.0-pro'

// Qwen 官方示例给的反向提示词
var DEFAULT_NEGATIVE_PROMPT =
  '低分辨率，低画质，肢体畸形，手指畸形，画面过饱和，蜡像感，人脸无细节，过度光滑，画面具有AI感。构图混乱。文字模糊，扭曲。'

function getDashScopeWanConfig() {
  return {
    baseURL: (process.env.VUE_APP_OFFICIAL_DASHSCOPE_WAN_BASE_URL || 'https://dashscope-intl.aliyuncs.com').replace(/\/$/, ''),
    apiKey: (process.env.VUE_APP_OFFICIAL_DASHSCOPE_WAN_API_KEY || '').trim()
  }
}

function normalizeCount(count) {
  var c = parseInt(count, 10)
  if (isNaN(c)) c = 1
  if (c < 1) c = 1
  if (c > 6) c = 6
  return c
}

function parseSize(sizeStr) {
  // Qwen 入参： '2048*2048'
  if (!sizeStr || typeof sizeStr !== 'string') return null
  var s = sizeStr.trim()
  var m = s.match(/^(\d+)\*(\d+)$/)
  if (!m) return null
  return { width: parseInt(m[1], 10), height: parseInt(m[2], 10) }
}

function guessExtAndMime(blob, url) {
  var mimeType = blob && blob.type ? String(blob.type) : ''
  var ext = 'png'
  if (mimeType) {
    if (mimeType.indexOf('jpeg') !== -1) { ext = 'jpg'; mimeType = 'image/jpeg' }
    else if (mimeType.indexOf('png') !== -1) { ext = 'png'; mimeType = 'image/png' }
    else if (mimeType.indexOf('webp') !== -1) { ext = 'webp'; mimeType = 'image/webp' }
  }
  if (!mimeType) {
    try {
      var path = new URL(url).pathname
      var m = path.match(/\.(png|jpe?g|webp)(?:\?|$)/i)
      if (m) {
        var e = String(m[1]).toLowerCase()
        ext = e === 'jpeg' ? 'jpg' : e
      }
    } catch (e) {}
  }
  if (!mimeType) {
    if (ext === 'jpg') mimeType = 'image/jpeg'
    else if (ext === 'webp') mimeType = 'image/webp'
    else mimeType = 'image/png'
  }
  return { ext: ext, mimeType: mimeType }
}

function extractDashScopeImageUrls(data, maxCount) {
  var urls = []
  var output = data && data.output ? data.output : null
  var choices = output && Array.isArray(output.choices) ? output.choices : []
  choices.forEach(function (choice) {
    if (urls.length >= maxCount) return
    var message = choice && choice.message ? choice.message : null
    var content = message && Array.isArray(message.content) ? message.content : []
    content.forEach(function (item) {
      if (urls.length >= maxCount) return
      var u = item && item.image ? String(item.image) : ''
      if (u) urls.push(u)
    })
  })
  return urls
}

function downloadUrlToBlob(url, baseName) {
  return fetch(url, { mode: 'cors' })
    .then(function (res) { return res.blob() })
    .then(function (blob) {
      if (!blob) return Promise.reject(new Error('download image failed: empty blob'))
      console.log('[QwenImage] image blob downloaded', blob.size, 'bytes')
      return blob
    })
}

/**
 * @param {Object} params - UI 传参
 * @param {string} params.prompt
 * @param {string} params.size - '2048*2048'
 * @param {number} params.count - n（1-6）
 * @returns {Promise<{ urls: string[], provider:'official', sync:true }>}
 */
export function submitDashScopeQwenImage20ProImage(params) {
  var cfg = getDashScopeWanConfig()
  if (!cfg.baseURL) return Promise.reject(new Error('未配置 VUE_APP_OFFICIAL_DASHSCOPE_WAN_BASE_URL'))
  if (!cfg.apiKey) return Promise.reject(new Error('未配置 VUE_APP_OFFICIAL_DASHSCOPE_WAN_API_KEY'))

  var prompt = (params.prompt || '').trim()
  if (!prompt) return Promise.reject(new Error('prompt 必填'))
  // 保证 prompt 长度满足 UI 限制，避免历史/展示与实际请求不一致
  prompt = prompt.slice(0, 800)

  var promptEnhance =
    params.promptEnhance !== undefined && params.promptEnhance !== null ? !!params.promptEnhance : true
  var watermark = params.watermark !== undefined && params.watermark !== null ? !!params.watermark : false
  var negativePromptRaw = params.negativePrompt != null ? String(params.negativePrompt) : ''
  var negativePromptTrimmed = negativePromptRaw.trim()
  var negativePrompt = negativePromptTrimmed
    ? negativePromptTrimmed.slice(0, 500)
    : String(DEFAULT_NEGATIVE_PROMPT).slice(0, 500)

  var count = normalizeCount(params.count)
  var size = String(params.size || '').trim()
  var parsedSize = parseSize(size)
  if (!parsedSize) return Promise.reject(new Error('size 必填且格式为 2048*2048'))
  if (!parsedSize.width || !parsedSize.height) return Promise.reject(new Error('size 解析失败'))

  var body = {
    model: DEFAULT_MODEL,
    input: {
      messages: [
        {
          role: 'user',
          content: [{ text: prompt }]
        }
      ]
    },
    parameters: {
      negative_prompt: negativePrompt,
      prompt_extend: promptEnhance,
      watermark: watermark,
      n: count,
      size: size
    }
  }

  return axios
    .post(cfg.baseURL + GENERATION_PATH, body, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + cfg.apiKey
      },
      timeout: 180000
    })
    .then(function (res) {
      var data = res && res.data ? res.data : null
      var urls = extractDashScopeImageUrls(data, count)
      if (!urls.length) return Promise.reject(new Error('官方接口未返回图片 URL'))

      var baseName = 'qwen-image-2-0-pro-' + Date.now()
      return Promise.all(
        urls.map(function (u, idx) {
          return downloadUrlToBlob(u, baseName + '-' + (idx + 1))
        })
      ).then(function (blobs) {
        return { urls: urls, blobs: blobs, provider: 'official', sync: true }
      })
    })
    .catch(function (err) {
      var msg = extractApiErrorMessage(err && err.response && err.response.data)
      msg = msg || (err && err.message) || '请求失败'
      return Promise.reject(new Error(msg))
    })
}

